import * as THREE from 'three/webgpu';
import {
  Fn,
  uniform,
  time,
  uv,
  attribute,
  positionLocal,
  normalLocal,
  float,
  vec2,
  vec3,
  abs,
  max,
  min,
  mix,
  sin,
  cos,
  fract,
  floor,
  length,
  dot,
  pow,
  mod,
  smoothstep,
} from 'three/tsl';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { ProceduralTerrain } from './ProceduralTerrain';
import { ProceduralOcean } from './ProceduralOcean';
import { sampleTerrainSlope, sampleTerrainWorldHeight } from './terrainProfile';
import {
  DEFAULT_VISUAL_QUALITY_PRESET,
  type VisualQualityPreset,
} from './visualConfig';
import { setEmissiveNode, hexDist, hexGrid, forceFieldPosition } from './tslUtils';
import {
  DEFAULT_WORLD_THEME,
  type WorldThemeDefinition,
} from './worldTheme';
import { ARENA_SIZE_X, ARENA_SIZE_Y, ARENA_SIZE_Z } from '../../shared/constants';

const ARENA_WIDTH = ARENA_SIZE_X;
const ARENA_HEIGHT = ARENA_SIZE_Y;
const ARENA_DEPTH = ARENA_SIZE_Z;
const POLLEN_COUNT = 220;

// ---------- TSL helpers for the pollen particle system --------------------

/** Animated drift offset for the pollen `Points` geometry. */
const pollenDrift = Fn(([speed, phase]: [ReturnType<typeof float>, ReturnType<typeof float>]) => {
  const tSpeed = time.mul(speed);
  return vec3(
    sin(tSpeed.add(phase)).mul(1.8),
    sin(tSpeed.mul(0.55).add(phase.mul(1.7))).mul(0.35).mul(0.6),
    cos(tSpeed.mul(0.75).add(phase.mul(0.6))).mul(1.8),
  );
});

function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

export class ArenaRenderer {
  private readonly scene: THREE.Scene;
  private readonly terrain: ProceduralTerrain;
  private readonly ocean: ProceduralOcean;
  private readonly sunPosition: THREE.Vector3;
  private readonly camera: THREE.PerspectiveCamera;
  private readonly qualityPreset: VisualQualityPreset;
  private readonly worldTheme: WorldThemeDefinition;
  private readonly wallMeshes: THREE.Mesh[] = [];
  private readonly scatterMeshes: THREE.InstancedMesh[] = [];

  private particles: THREE.Points;
  private wallMat!: THREE.MeshBasicNodeMaterial;
  private ceilingMat!: THREE.MeshBasicNodeMaterial;

  constructor(
    scene: THREE.Scene,
    sunPosition: THREE.Vector3,
    camera: THREE.PerspectiveCamera,
    qualityPreset: VisualQualityPreset = DEFAULT_VISUAL_QUALITY_PRESET,
    worldTheme: WorldThemeDefinition = DEFAULT_WORLD_THEME,
  ) {
    this.scene = scene;
    this.sunPosition = sunPosition;
    this.camera = camera;
    this.qualityPreset = qualityPreset;
    this.worldTheme = worldTheme;

    this.terrain = new ProceduralTerrain(scene, worldTheme, qualityPreset);
    this.ocean = new ProceduralOcean(scene, worldTheme);
    this.buildBoundaries(scene);
    this.particles = this.buildAtmosphere(scene);

    if (qualityPreset.features.worldScatter) {
      this.buildScatter(scene);
    }
  }

  update(deltaMs: number): void {
    // TSL `time` is auto-updated by the renderer; only child renderers need delta.
    this.terrain.update(deltaMs, this.sunPosition, this.camera.position);
    this.ocean.update(deltaMs, this.sunPosition, this.camera.position);
  }

  dispose(): void {
    this.terrain.dispose();
    this.ocean.dispose();

    for (const mesh of this.wallMeshes) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
    }
    this.wallMeshes.length = 0;
    this.wallMat.dispose();
    this.ceilingMat.dispose();

    for (const mesh of this.scatterMeshes) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    this.scatterMeshes.length = 0;

    this.scene.remove(this.particles);
    this.particles.geometry.dispose();
    (this.particles.material as THREE.Material).dispose();
  }

  private buildBoundaries(scene: THREE.Scene): void {
    const halfW = ARENA_WIDTH / 2;
    const halfD = ARENA_DEPTH / 2;

    const wallConfigs: Array<{
      width: number;
      height: number;
      pos: [number, number, number];
      rot: [number, number, number];
    }> = [
      { width: ARENA_DEPTH, height: ARENA_HEIGHT, pos: [halfW, ARENA_HEIGHT / 2, 0], rot: [0, -Math.PI / 2, 0] },
      { width: ARENA_DEPTH, height: ARENA_HEIGHT, pos: [-halfW, ARENA_HEIGHT / 2, 0], rot: [0, Math.PI / 2, 0] },
      { width: ARENA_WIDTH, height: ARENA_HEIGHT, pos: [0, ARENA_HEIGHT / 2, halfD], rot: [0, Math.PI, 0] },
      { width: ARENA_WIDTH, height: ARENA_HEIGHT, pos: [0, ARENA_HEIGHT / 2, -halfD], rot: [0, 0, 0] },
      { width: ARENA_WIDTH, height: ARENA_DEPTH, pos: [0, ARENA_HEIGHT, 0], rot: [Math.PI / 2, 0, 0] },
    ];

    const fieldColor = uniform(new THREE.Color(this.worldTheme.forceField.color));

    const makeFieldMat = (baseOpacity: number) => {
      const mat = new THREE.MeshBasicNodeMaterial({
        transparent: true,
        side: THREE.DoubleSide,
        depthWrite: false,
        blending: THREE.NormalBlending,
      });

      mat.positionNode = forceFieldPosition();

      const opacityUniform = uniform(baseOpacity);

      // Inlined (not module-scoped Fn) because it reads the wall's own
      // `uv()` which is only valid inside a material's fragment graph.
      const uvNode = uv().toVar();
      const hex = hexGrid(uvNode, float(14.0));
      const ripple = sin(
        length(uvNode.sub(vec2(0.5, 0.5))).mul(28.0).sub(time.mul(3.3)),
      ).mul(0.5).add(0.5);
      const scan = sin(uvNode.y.mul(140.0).add(time.mul(5.0))).mul(0.5).add(0.5);
      const edgeDist = min(
        min(uvNode.x, uvNode.x.oneMinus()),
        min(uvNode.y, uvNode.y.oneMinus()),
      );
      const edgeGlow = smoothstep(float(0.0), float(0.16), edgeDist).oneMinus();
      const intensity = hex.mul(0.45)
        .add(edgeGlow.mul(0.65))
        .add(ripple.mul(0.18))
        .add(scan.mul(0.08));

      const fragColor = fieldColor.mul(float(0.55).add(intensity));
      const fragAlpha = opacityUniform.add(intensity.mul(0.13));

      mat.colorNode = fragColor;
      setEmissiveNode(mat, fragColor);
      mat.opacityNode = fragAlpha;

      return mat;
    };

    this.wallMat = makeFieldMat(0.05);
    this.ceilingMat = makeFieldMat(0.065);

    for (const cfg of wallConfigs) {
      const wallGeo = new THREE.PlaneGeometry(cfg.width, cfg.height);
      const isCeiling = cfg.pos[1] === ARENA_HEIGHT;
      const wall = new THREE.Mesh(wallGeo, isCeiling ? this.ceilingMat : this.wallMat);
      wall.position.set(...cfg.pos);
      wall.rotation.set(...cfg.rot);
      scene.add(wall);
      this.wallMeshes.push(wall);
    }
  }

  private buildAtmosphere(scene: THREE.Scene): THREE.Points {
    const count = Math.round(POLLEN_COUNT * this.qualityPreset.scatterDensity);
    const positions = new Float32Array(count * 3);
    const phases = new Float32Array(count);
    const speeds = new Float32Array(count);
    const rand = mulberry32(78193);

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      positions[i3] = (rand() - 0.5) * ARENA_WIDTH * 1.6;
      positions[i3 + 1] = 2 + rand() * 34;
      positions[i3 + 2] = (rand() - 0.5) * ARENA_DEPTH * 1.6;
      phases[i] = rand() * Math.PI * 2;
      speeds[i] = 0.8 + rand() * 1.8;
    }

    const particleGeo = new THREE.BufferGeometry();
    particleGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    particleGeo.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));
    particleGeo.setAttribute('aSpeed', new THREE.BufferAttribute(speeds, 1));

    const particleMat = new THREE.PointsNodeMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const phaseAttr = float(attribute('aPhase', 'float'));
    const speedAttr = float(attribute('aSpeed', 'float'));

    particleMat.positionNode = positionLocal.add(pollenDrift(speedAttr, phaseAttr));

    // sizeNode is assigned for forward-compatibility (PointsNodeMaterial's
    // sizeNode is currently ignored under the WebGL backend for Points).
    const sizePulse = sin(time.mul(speedAttr).add(phaseAttr)).mul(0.45).add(0.55);
    particleMat.sizeNode = float(0.42).mul(sizePulse);

    const sparkle = pow(
      max(sin(phaseAttr.add(time.mul(3.2))), float(0.0)),
      float(8.0),
    );

    // pointUV is invalid WGSL; 1px WebGPU points need no circle softening
    const soft = float(1.0);

    const baseCol = uniform(new THREE.Color(0xf6f7ec));
    const sparkleCol = uniform(new THREE.Color(this.worldTheme.forceField.sparkle));
    const tintedColor = mix(baseCol, sparkleCol, sparkle.mul(0.55));

    particleMat.colorNode = tintedColor;
    setEmissiveNode(particleMat, tintedColor);
    particleMat.opacityNode = float(0.12).add(sparkle.mul(0.55)).mul(soft);

    const particles = new THREE.Points(particleGeo, particleMat);
    scene.add(particles);
    return particles;
  }

  private buildScatter(scene: THREE.Scene): void {
    this.buildRingLandmarks(scene);
    this.buildRandomScatter(scene);
  }

  private buildRingLandmarks(scene: THREE.Scene): void {
    const count = Math.max(8, Math.round(this.worldTheme.scatter.landmarkCount * this.qualityPreset.scatterDensity));
    const slab = new THREE.BoxGeometry(4.4, 0.8, 3.6);
    const core = new THREE.CylinderGeometry(0.42, 0.62, 1.2, 6);
    core.translate(0, 0.8, 0);
    const geometry = mergeGeometries([slab.toNonIndexed(), core.toNonIndexed()], false);
    slab.dispose();
    core.dispose();

    const material = new THREE.MeshStandardNodeMaterial({
      color: new THREE.Color(0x6d737a),
      roughness: 0.58,
      metalness: 0.48,
      flatShading: true,
    });

    const mesh = new THREE.InstancedMesh(geometry, material, count);
    const matrix = new THREE.Matrix4();
    const position = new THREE.Vector3();
    const scale = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();

    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const radius = this.worldTheme.terrain.innerClearRadius + 10 + (i % 3) * 4.5;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      const y = sampleTerrainWorldHeight(x, z, this.worldTheme);

      position.set(x, y + 0.55, z);
      quaternion.setFromEuler(new THREE.Euler(0, -angle + Math.PI / 2, 0));
      scale.set(
        0.95 + (i % 3) * 0.15,
        0.9 + (i % 2) * 0.12,
        0.9 + ((i + 1) % 3) * 0.08,
      );
      matrix.compose(position, quaternion, scale);
      mesh.setMatrixAt(i, matrix);
    }

    mesh.computeBoundingSphere();
    scene.add(mesh);
    this.scatterMeshes.push(mesh);
  }

  private buildRandomScatter(scene: THREE.Scene): void {
    const rand = mulberry32(50123);
    const outerMinRadius = this.worldTheme.terrain.landmarkRadius + 28;
    const outerMaxRadius = this.worldTheme.scatter.maxRadius;

    const rockMaterial = new THREE.MeshStandardNodeMaterial({
      color: new THREE.Color(this.worldTheme.terrain.colors.rock),
      roughness: 0.85,
      metalness: 0.18,
      flatShading: true,
    });
    const debrisMaterial = new THREE.MeshStandardNodeMaterial({
      color: new THREE.Color(0x6d6f76),
      roughness: 0.54,
      metalness: 0.46,
      flatShading: true,
    });
    const foliageMaterial = new THREE.MeshStandardNodeMaterial({
      color: new THREE.Color(this.worldTheme.terrain.colors.grassLight),
      roughness: 0.92,
      metalness: 0.04,
      flatShading: true,
    });
    // `emissive` + `emissiveIntensity` feed the MRT emissive channel for
    // selective bloom without needing an explicit `emissiveNode`.
    const crystalMaterial = new THREE.MeshStandardNodeMaterial({
      color: new THREE.Color(this.worldTheme.terrain.colors.crystal),
      emissive: new THREE.Color(this.worldTheme.terrain.colors.crystal),
      emissiveIntensity: 0.85,
      roughness: 0.16,
      metalness: 0.62,
      flatShading: true,
    });

    const rockGeometry = new THREE.DodecahedronGeometry(1.05, 0);
    const debrisGeometry = new THREE.TetrahedronGeometry(0.85, 0);
    const crystalSpire = new THREE.OctahedronGeometry(0.95, 0);
    crystalSpire.scale(0.75, 2.1, 0.75);
    const crystalBase = new THREE.CylinderGeometry(0.35, 0.55, 0.4, 6);
    crystalBase.translate(0, -1.0, 0);
    const crystalGeometry = mergeGeometries([crystalSpire.toNonIndexed(), crystalBase.toNonIndexed()], false);
    crystalSpire.dispose();
    crystalBase.dispose();

    const bladeA = new THREE.BoxGeometry(0.12, 1.3, 0.24);
    const bladeB = bladeA.clone();
    bladeB.rotateY(Math.PI / 3);
    const bladeC = bladeA.clone();
    bladeC.rotateY(-Math.PI / 3);
    const foliageGeometry = mergeGeometries([
      bladeA.toNonIndexed(),
      bladeB.toNonIndexed(),
      bladeC.toNonIndexed(),
    ], false);
    bladeA.dispose();
    bladeB.dispose();
    bladeC.dispose();

    this.scatterMeshes.push(
      this.createScatterMesh(
        scene,
        rockGeometry,
        rockMaterial,
        Math.round(this.worldTheme.scatter.rockCount * this.qualityPreset.scatterDensity),
        rand,
        outerMinRadius,
        outerMaxRadius,
        0.78,
        1.9,
      ),
      this.createScatterMesh(
        scene,
        debrisGeometry,
        debrisMaterial,
        Math.round(this.worldTheme.scatter.debrisCount * this.qualityPreset.scatterDensity),
        rand,
        outerMinRadius,
        outerMaxRadius,
        0.4,
        1.3,
      ),
      this.createScatterMesh(
        scene,
        foliageGeometry,
        foliageMaterial,
        Math.round(this.worldTheme.scatter.foliageCount * this.qualityPreset.scatterDensity),
        rand,
        outerMinRadius,
        outerMaxRadius,
        0.75,
        1.55,
        0.5,
      ),
    );

    const crystalMesh = this.createScatterMesh(
      scene,
      crystalGeometry,
      crystalMaterial,
      Math.round(this.worldTheme.scatter.crystalCount * this.qualityPreset.scatterDensity),
      rand,
      outerMinRadius + 36,
      outerMaxRadius,
      0.8,
      1.9,
      0.42,
    );
    this.scatterMeshes.push(crystalMesh);
  }

  private createScatterMesh(
    scene: THREE.Scene,
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    count: number,
    rand: () => number,
    minRadius: number,
    maxRadius: number,
    minScale: number,
    maxScale: number,
    maxSlope: number = 0.7,
  ): THREE.InstancedMesh {
    const mesh = new THREE.InstancedMesh(geometry, material, count);
    const position = new THREE.Vector3();
    const scale = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const matrix = new THREE.Matrix4();
    let placed = 0;
    let attempts = 0;

    while (placed < count && attempts < count * 12) {
      attempts++;
      const angle = rand() * Math.PI * 2;
      const radius = THREE.MathUtils.lerp(minRadius, maxRadius, Math.sqrt(rand()));
      const x = Math.cos(angle) * radius + (rand() - 0.5) * 16;
      const z = Math.sin(angle) * radius + (rand() - 0.5) * 16;
      const slope = sampleTerrainSlope(x, z, this.worldTheme);
      if (slope > maxSlope) continue;

      const y = sampleTerrainWorldHeight(x, z, this.worldTheme);
      position.set(x, y, z);
      quaternion.setFromEuler(new THREE.Euler(
        rand() * 0.25,
        rand() * Math.PI * 2,
        (rand() - 0.5) * 0.25,
      ));

      const uniformScale = THREE.MathUtils.lerp(minScale, maxScale, rand());
      scale.set(
        uniformScale * THREE.MathUtils.lerp(0.8, 1.2, rand()),
        uniformScale * THREE.MathUtils.lerp(0.85, 1.5, rand()),
        uniformScale * THREE.MathUtils.lerp(0.8, 1.2, rand()),
      );
      matrix.compose(position, quaternion, scale);
      mesh.setMatrixAt(placed, matrix);
      placed++;
    }

    mesh.count = placed;
    mesh.computeBoundingSphere();
    scene.add(mesh);
    return mesh;
  }
}
